import { useState } from 'react';

import { useAuditLog } from '../../hooks/useAdmin';
import type { AuditEntityType, AuditEntry } from '../../types';
import styles from './AuditHistory.module.css';

interface Props {
  entityType: AuditEntityType;
  entityId: number;
  /** Default expanded state. Off keeps the panel collapsed under a
   * "History (N)" toggle so it doesn't crowd small detail panes. */
  defaultExpanded?: boolean;
}

/**
 * Generic audit-log timeline. Used today for suggestions + intel chunks;
 * any new audited entity (roster, schedules, conferences) drops in by
 * passing its entity_type / entity_id — no per-domain UI to write.
 *
 * Renders newest-first. Each row carries action type, actor identity
 * (with impersonation indicator if effective != actual), timestamp, and
 * an inline diff. Click to expand for the full prior/new JSON.
 */
export function AuditHistory({ entityType, entityId, defaultExpanded = false }: Props) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const query = useAuditLog(entityType, entityId);
  const data = query.data;

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={styles.toggleArrow}>{expanded ? '▾' : '▸'}</span>
        History
        {data && <span className={styles.count}> ({data.total})</span>}
      </button>
      {expanded && (
        <div className={styles.body}>
          {query.isLoading && <div className={styles.muted}>Loading…</div>}
          {query.isError && (
            <div className={styles.errorMessage}>
              Failed to load history: {(query.error as Error)?.message ?? 'unknown'}
            </div>
          )}
          {data && data.entries.length === 0 && (
            <div className={styles.muted}>
              No audit rows yet — history starts when the next mutation lands.
            </div>
          )}
          {data && data.entries.length > 0 && (
            <div className={styles.timeline}>
              {data.entries.map((e) => (
                <AuditRow key={e.id} entry={e} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// One row per audit entry — collapsed summary, click to expand for diff.
// ─────────────────────────────────────────────────────────────────────────

function AuditRow({ entry }: { entry: AuditEntry }) {
  const [expanded, setExpanded] = useState(false);
  const hasDiff = entry.fields_changed.length > 0
    || (entry.details && Object.keys(entry.details).length > 0);
  return (
    <div className={`${styles.row} ${styles[`actor_${entry.actor_via}`] || ''}`}>
      <div
        className={styles.rowHeader}
        onClick={hasDiff ? () => setExpanded((v) => !v) : undefined}
        style={hasDiff ? { cursor: 'pointer' } : undefined}
      >
        <span className={styles.actionBadge}>
          {actionIcon(entry.action_type)} {entry.action_type}
        </span>
        <span className={styles.actor}>{actorLabel(entry)}</span>
        <span className={styles.timestamp}>{formatTime(entry.created_at)}</span>
        {entry.fields_changed.length > 0 && (
          <span className={styles.fields}>
            {entry.fields_changed.slice(0, 5).join(', ')}
            {entry.fields_changed.length > 5 && ` +${entry.fields_changed.length - 5} more`}
          </span>
        )}
      </div>
      {expanded && hasDiff && (
        <div className={styles.diff}>
          {entry.fields_changed.length > 0 && (
            <div>
              <div className={styles.diffLabel}>Diff</div>
              {entry.fields_changed.map((field) => (
                <div key={field} className={styles.diffField}>
                  <span className={styles.diffFieldName}>{field}</span>
                  <span className={styles.diffPrior}>
                    {formatValue(entry.prior_values?.[field])}
                  </span>
                  <span className={styles.diffArrow}>→</span>
                  <span className={styles.diffNew}>
                    {formatValue(entry.new_values?.[field])}
                  </span>
                </div>
              ))}
            </div>
          )}
          {entry.details && Object.keys(entry.details).length > 0 && (
            <div>
              <div className={styles.diffLabel}>Details</div>
              <pre className={styles.detailsJson}>
                {JSON.stringify(entry.details, null, 2)}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Display helpers
// ─────────────────────────────────────────────────────────────────────────

function actionIcon(action: string): string {
  // One-glyph hint per action_type so a quick scan tells you whether
  // history is mostly creates, edits, or auto-discards.
  const map: Record<string, string> = {
    created: '＋',
    edit: '✎',
    dismiss: '✗',
    approve: '✓',
    unapprove: '↺',
    push_affinity: '⬆',
    log_pass: '⏭',
    reclassify: '↻',
    recheck_dedupe: '⇆',
    auto_discarded: '⊘',
    consolidated: '∪',
    backlog_flag: '⚑',
    field_change: '◧',
    drafted_from_url: '🔗',
    drafted_from_text: '📝',
    delete: '🗑',
    week_shifted: '↦',
  };
  return map[action] ?? '·';
}

function actorLabel(entry: AuditEntry): React.ReactNode {
  if (entry.actor_via === 'system') {
    const cause = (entry.details as { cause?: string } | null)?.cause;
    return (
      <span className={styles.actorSystem}>
        system{cause ? `:${cause}` : ''}
      </span>
    );
  }
  const effective = entry.actor_email ?? '—';
  const actual = entry.actor_email_actual ?? effective;
  if (entry.actor_via === 'user_triggered') {
    return (
      <span>
        <code className={styles.actorEmail}>{shortEmail(effective)}</code>
        <span className={styles.actorBadge}>via trigger</span>
      </span>
    );
  }
  // 'user' — direct action. If effective != actual we'd surface the
  // impersonation, but Phase B blocks impersonated writes so this
  // should never happen in practice. Defensive render anyway.
  if (effective !== actual) {
    return (
      <span>
        <code className={styles.actorEmail}>{shortEmail(effective)}</code>
        <span className={styles.actorBadge}>as {shortEmail(actual)}</span>
      </span>
    );
  }
  return <code className={styles.actorEmail}>{shortEmail(effective)}</code>;
}

function shortEmail(e: string): string {
  if (!e || !e.includes('@')) return e;
  return e.split('@')[0];
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const absMin = Math.round(Math.abs(diffMs) / 60_000);
  if (absMin < 1) return 'just now';
  if (absMin < 60) return `${absMin}m ago`;
  if (absMin < 24 * 60) {
    return new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/New_York',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(d);
  }
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

function formatValue(v: unknown): string {
  if (v === null || v === undefined) return '—';
  if (v === '') return '(empty)';
  if (Array.isArray(v)) return v.length === 0 ? '[]' : v.join(', ');
  if (typeof v === 'object') return JSON.stringify(v);
  if (typeof v === 'string' && v.length > 80) return v.slice(0, 77) + '…';
  return String(v);
}
